import React, { useState } from 'react';
import { Info, X } from 'lucide-react';

interface ModuleInfoModalProps {
    title: string;
    description: string;
    description2?: string;
    benefits: string[];
    usageInstructions?: string[];
}

export const ModuleInfoModal: React.FC<ModuleInfoModalProps> = ({ title, description, description2, benefits, usageInstructions }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <>
            <button
                onClick={() => setIsOpen(true)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors border border-blue-200"
                title={`${title} Information`}
            >
                <Info size={16} />
                <span>Info</span>
            </button>

            {isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
                    <div
                        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden animate-slideUp"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex justify-between items-center">
                            <div className="flex items-center gap-3 text-white">
                                <div className="p-2 bg-white/20 rounded-lg backdrop-blur-md">
                                    <Info size={24} />
                                </div>
                                <h2 className="text-xl font-bold">{title} Guide</h2>
                            </div>
                            <button
                                onClick={() => setIsOpen(false)}
                                className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-full transition-colors"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-6 max-h-[80vh] overflow-y-auto space-y-6">

                            {/* What the module does */}
                            <section>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                                    <span className="w-1 h-6 bg-blue-500 rounded-full"></span>
                                    Overview
                                </h3>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-sm">
                                    {description}
                                </p>
                                {description2 && (
                                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-sm mt-2">
                                        {description2}
                                    </p>
                                )}
                            </section>

                            {/* Benefits & Insights */}
                            <section className="bg-blue-50 dark:bg-blue-900/10 rounded-xl p-5 border border-blue-100 dark:border-blue-800">
                                <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
                                    <span className="text-xl">✨</span> Benefits & Key Insights
                                </h3>
                                <ul className="space-y-2">
                                    {benefits.map((benefit, idx) => (
                                        <li key={idx} className="flex items-start gap-2 text-sm text-blue-800 dark:text-blue-200">
                                            <span className="mt-1 w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0" />
                                            <span>{benefit}</span>
                                        </li>
                                    ))}
                                </ul>
                            </section>

                            {/* How to Use */}
                            {usageInstructions && usageInstructions.length > 0 && (
                                <section>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                        <span className="text-xl">🛠️</span> How to Use
                                    </h3>
                                    <div className="space-y-3">
                                        {usageInstructions.map((step, idx) => (
                                            <div key={idx} className="flex gap-3 text-sm group">
                                                <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex items-center justify-center font-mono font-bold text-xs group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors flex-shrink-0">
                                                    {idx + 1}
                                                </div>
                                                <p className="text-gray-600 dark:text-gray-300 pt-0.5">{step}</p>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}

                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 flex justify-end">
                            <button
                                onClick={() => setIsOpen(false)}
                                className="px-4 py-2 bg-gray-900 dark:bg-gray-700 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
                            >
                                Got it
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
